import React, { useState, useMemo } from "react";
import {
  CurrencyDollarIcon,
  ArchiveBoxArrowDownIcon,
  CalendarDaysIcon,
  FunnelIcon,
  ArrowDownCircleIcon,
  PaperClipIcon,
  CalculatorIcon,
  MagnifyingGlassIcon,
  ArrowPathIcon,
  ClockIcon,
  DocumentArrowDownIcon,
  TableCellsIcon,
  UserIcon,
  EyeIcon,
} from "@heroicons/react/24/outline";

// --- GLOBAL MOCK DATA ---
const MOCK_PAYROLL_SUMMARY = {
    totalGrossPayout: 285000,
    totalNetPayout: 235000,
    totalTaxDeducted: 45000,
    totalReimbursements: 5000,
};

const MOCK_EMPLOYEES = [
    { id: 1, name: "Arjun Singh", designation: "Sr. Engineer", department: "Tech", status: "Paid" },
    { id: 2, name: "Priya Sharma", designation: "HR Manager", department: "HR", status: "Paid" },
    { id: 3, name: "Vikram Kumar", designation: "Sales Associate", department: "Sales", status: "Pending" },
    { id: 4, name: "Ravi Menon", designation: "Jr. Analyst", department: "Finance", status: "Paid" },
    { id: 5, name: "Sonia Das", designation: "Marketing Lead", department: "Marketing", status: "Paid" },
];

const MOCK_DETAILED_REPORTS = MOCK_EMPLOYEES.map(emp => ({
    ...emp,
    grossSalary: 6500,
    deductions: 1200,
    netPay: 5300,
    components: {
        basic: 4000,
        hra: 1000,
        bonus: 500,
        tax: 900,
        pf: 300,
    }
}));

const MOCK_ACTIVITY_LOGS = [
    { id: 1, employeeId: "EMP001", action: "Payroll Generated", modifiedBy: "HR Admin", date: "2024-01-15", details: "Monthly payroll processed for January 2024" },
    { id: 2, employeeId: "EMP002", action: "Salary Updated", modifiedBy: "Finance Manager", date: "2024-01-14", details: "Basic salary increased from ₹45,000 to ₹50,000" },
    { id: 3, employeeId: "EMP003", action: "Deduction Added", modifiedBy: "HR Admin", date: "2024-01-13", details: "Medical insurance deduction added" },
    { id: 4, employeeId: "EMP001", action: "Bonus Applied", modifiedBy: "CEO", date: "2024-01-12", details: "Performance bonus of ₹10,000 applied" },
    { id: 5, employeeId: "EMP004", action: "Leave Deduction", modifiedBy: "HR Manager", date: "2024-01-11", details: "Unpaid leave deduction for 2 days" },
];

// --- HELPER COMPONENTS ---

// Summary Card Component (Adapted for Payroll)
const PayrollMetricCard = ({ title, value, icon: Icon, colorClass, footer }) => (
    <div className="bg-white p-5 rounded-xl shadow-lg border border-gray-100 flex flex-col justify-between h-full transition duration-300 hover:shadow-indigo-200/50 hover:border-indigo-400 transform hover:-translate-y-0.5">
        <div className="flex items-start justify-between">
            <div>
                <p className="text-sm font-poppins font-medium text-gray-500">{title}</p>
                <p className="text-3xl font-poppins font-bold text-gray-900 mt-2">
                    {value.toLocaleString('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 0 })}
                </p>
            </div>
            <div className={`p-3 rounded-xl ${colorClass}/10`}>
                <Icon className={`h-7 w-7 ${colorClass}`} />
            </div>
        </div>
        <p className="text-xs font-roboto mt-4 text-gray-400 border-t pt-2">{footer}</p>
    </div>
);

// Custom Select Component (Reusable)
const CustomSelect = ({ label, options, selected, onChange }) => (
    <div className="flex flex-col">
        <label className="text-xs font-poppins font-semibold text-gray-600 mb-1">{label}</label>
        <select
            value={selected}
            onChange={onChange}
            className="w-full p-2 border border-gray-300 rounded-lg bg-white text-sm font-roboto focus:ring-indigo-500 focus:border-indigo-500 transition duration-150 shadow-sm"
        >
            {options.map((option, index) => (
                <option key={index} value={option.value || option.label}>
                    {option.label}
                </option>
            ))}
        </select>
    </div>
);

// --- MAIN REPORT COMPONENT ---
const DetailedPayrollReport = ({ data, isFiltering }) => {
    
    // State for managing the expanded view of a payroll row
    const [expandedRow, setExpandedRow] = useState(null);

    const toggleExpansion = (id) => {
        setExpandedRow(expandedRow === id ? null : id);
    };

    if (isFiltering) {
        return (
            <div className="text-center py-20 text-indigo-500 bg-white rounded-xl shadow-lg mt-6">
                <ArrowPathIcon className="h-8 w-8 mx-auto animate-spin mb-4" />
                <p className="text-lg font-poppins">Loading report data...</p>
            </div>
        );
    }
    
    return (
        <div className="bg-white p-6 rounded-xl shadow-2xl border border-gray-100 mt-6"> 
            <h3 className="text-xl font-poppins font-bold text-gray-800 mb-6 flex items-center">
                <ArchiveBoxArrowDownIcon className="h-6 w-6 mr-2 text-indigo-600"/>
                Employee Payout Details
            </h3>

            <div className="overflow-x-auto rounded-xl border border-gray-200 shadow-md">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-100/70">
                        <tr>
                            <th className="px-4 py-3 text-left text-xs font-poppins font-bold text-gray-700 uppercase tracking-wider w-1/4">Employee</th>
                            <th className="px-4 py-3 text-left text-xs font-poppins font-bold text-gray-700 uppercase tracking-wider">Designation</th>
                            <th className="px-4 py-3 text-center text-xs font-poppins font-bold text-gray-700 uppercase tracking-wider">Gross Pay</th>
                            <th className="px-4 py-3 text-center text-xs font-poppins font-bold text-gray-700 uppercase tracking-wider">Deductions</th>
                            <th className="px-4 py-3 text-center text-xs font-poppins font-bold text-gray-700 uppercase tracking-wider font-extrabold">Net Pay</th>
                            <th className="px-4 py-3 text-center text-xs font-poppins font-bold text-gray-700 uppercase tracking-wider">Status</th>
                            <th className="px-4 py-3 text-center text-xs font-poppins font-bold text-gray-700 uppercase tracking-wider">Action</th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-100 font-roboto">
                        {data.map((report) => (
                            <React.Fragment key={report.id}>
                                <tr className="hover:bg-blue-50 transition duration-100"> 
                                    <td className="px-4 py-3 text-sm text-gray-900 font-semibold">{report.name}</td>
                                    <td className="px-4 py-3 text-sm text-gray-700">{report.designation}</td>
                                    <td className="px-4 py-3 text-center whitespace-nowrap text-sm text-gray-900">
                                        {report.grossSalary.toLocaleString('en-IN')}
                                    </td>
                                    <td className="px-4 py-3 text-center whitespace-nowrap text-sm text-red-600">
                                        {report.deductions.toLocaleString('en-IN')}
                                    </td>
                                    <td className="px-4 py-3 text-center whitespace-nowrap text-sm font-extrabold text-green-700">
                                        {report.netPay.toLocaleString('en-IN')}
                                    </td>
                                    <td className="px-4 py-3 text-center whitespace-nowrap text-xs">
                                        <span className={`px-3 py-1 rounded-full font-bold ${
                                            report.status === 'Paid' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                                        }`}>
                                            {report.status}
                                        </span>
                                    </td>
                                    <td className="px-4 py-3 text-center">
                                        <button 
                                            onClick={() => toggleExpansion(report.id)}
                                            className="text-indigo-600 hover:text-indigo-800 transition duration-150 text-sm font-semibold"
                                        >
                                            {expandedRow === report.id ? 'Hide Details' : 'View Details'}
                                        </button>
                                    </td>
                                </tr>

                                {/* Expanded Row for Detailed Components */}
                                {expandedRow === report.id && (
                                    <tr className="bg-gray-50 border-t border-indigo-100">
                                        <td colSpan="7" className="p-4">
                                            <div className="flex flex-wrap gap-x-8 gap-y-2 text-xs font-roboto">
                                                <p className="w-full text-sm font-bold text-indigo-700 mb-2">Pay Components:</p>
                                                {Object.entries(report.components).map(([key, value]) => (
                                                    <div key={key} className="w-1/4 sm:w-auto flex-grow flex justify-between items-center bg-white p-3 rounded-lg border border-gray-100 shadow-inner">
                                                        <span className="capitalize font-semibold text-gray-600">{key}:</span>
                                                        <span className={`ml-2 font-bold ${key === 'tax' || key === 'pf' ? 'text-red-600' : 'text-green-700'}`}>
                                                            {value.toLocaleString('en-IN')}
                                                        </span>
                                                    </div>
                                                ))}
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </React.Fragment>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

// Activity Logs Component
const ActivityLogs = () => {
    return (
        <div className="bg-white p-6 rounded-xl shadow-2xl border border-gray-100 mt-6">
            <h3 className="text-xl font-poppins font-bold text-gray-800 mb-6 flex items-center">
                <ClockIcon className="h-6 w-6 mr-2 text-indigo-600"/>
                Activity Logs
            </h3>
            <div className="overflow-x-auto rounded-xl border border-gray-200 shadow-md">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-100/70">
                        <tr>
                            <th className="px-4 py-3 text-left text-xs font-poppins font-bold text-gray-700 uppercase tracking-wider">Employee ID</th>
                            <th className="px-4 py-3 text-left text-xs font-poppins font-bold text-gray-700 uppercase tracking-wider">Action</th>
                            <th className="px-4 py-3 text-left text-xs font-poppins font-bold text-gray-700 uppercase tracking-wider">Modified By</th>
                            <th className="px-4 py-3 text-left text-xs font-poppins font-bold text-gray-700 uppercase tracking-wider">Date</th>
                            <th className="px-4 py-3 text-left text-xs font-poppins font-bold text-gray-700 uppercase tracking-wider">Details</th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-100 font-roboto">
                        {MOCK_ACTIVITY_LOGS.map((log) => (
                            <tr key={log.id} className="hover:bg-blue-50 transition duration-100">
                                <td className="px-4 py-3 text-sm text-gray-900 font-semibold">{log.employeeId}</td>
                                <td className="px-4 py-3 text-sm text-gray-700">{log.action}</td>
                                <td className="px-4 py-3 text-sm text-gray-700">{log.modifiedBy}</td>
                                <td className="px-4 py-3 text-sm text-gray-700">{log.date}</td>
                                <td className="px-4 py-3 text-sm text-gray-600">{log.details}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

// Reports Component
const Reports = () => {
    const [reportType, setReportType] = useState('daily');
    const [customStartDate, setCustomStartDate] = useState('');
    const [customEndDate, setCustomEndDate] = useState('');
    const [isGenerating, setIsGenerating] = useState(false);

    const reportOptions = [
        { label: "Daily", value: "daily" },
        { label: "Monthly", value: "monthly" },
        { label: "Quarterly", value: "quarterly" },
        { label: "Half Yearly", value: "half-yearly" },
        { label: "Yearly", value: "yearly" },
        { label: "Custom Date Range", value: "custom" },
    ];

    const handleDownload = (format) => {
        setIsGenerating(true);
        console.log(`Generating ${reportType} report in ${format} format`);
        setTimeout(() => {
            setIsGenerating(false);
            alert(`${reportType.charAt(0).toUpperCase() + reportType.slice(1)} Payroll Report downloaded in ${format.toUpperCase()} format!`);
        }, 2000);
    };

    return (
        <div className="bg-white p-6 rounded-xl shadow-2xl border border-gray-100 mt-6">
            <h3 className="text-xl font-poppins font-bold text-gray-800 mb-6 flex items-center">
                <DocumentArrowDownIcon className="h-6 w-6 mr-2 text-indigo-600"/>
                Reports
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
                <CustomSelect
                    label="Report Type"
                    options={reportOptions}
                    selected={reportType}
                    onChange={(e) => setReportType(e.target.value)}
                />
                
                {reportType === 'custom' && (
                    <>
                        <div className="flex flex-col">
                            <label className="text-xs font-poppins font-semibold text-gray-600 mb-1">Start Date</label>
                            <input
                                type="date"
                                value={customStartDate}
                                onChange={(e) => setCustomStartDate(e.target.value)}
                                className="w-full p-2 border border-gray-300 rounded-lg bg-white text-sm font-roboto focus:ring-indigo-500 focus:border-indigo-500 transition duration-150 shadow-sm"
                            />
                        </div>
                        <div className="flex flex-col">
                            <label className="text-xs font-poppins font-semibold text-gray-600 mb-1">End Date</label>
                            <input
                                type="date"
                                value={customEndDate}
                                onChange={(e) => setCustomEndDate(e.target.value)}
                                className="w-full p-2 border border-gray-300 rounded-lg bg-white text-sm font-roboto focus:ring-indigo-500 focus:border-indigo-500 transition duration-150 shadow-sm"
                            />
                        </div>
                    </>
                )}
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
                <button
                    onClick={() => handleDownload('excel')}
                    disabled={isGenerating}
                    className={`px-6 py-3 text-sm font-semibold rounded-lg transition duration-300 shadow-lg flex items-center justify-center bg-green-600 text-white hover:bg-green-700 focus:ring-4 focus:ring-green-300 transform active:scale-95 ${isGenerating ? 'opacity-60 cursor-not-allowed' : ''}`}
                >
                    {isGenerating ? (
                        <ArrowPathIcon className="h-5 w-5 mr-2 animate-spin" />
                    ) : (
                        <TableCellsIcon className="h-5 w-5 mr-2" />
                    )}
                    {isGenerating ? 'Generating...' : 'Download Excel'}
                </button>
                
                <button
                    onClick={() => handleDownload('pdf')}
                    disabled={isGenerating}
                    className={`px-6 py-3 text-sm font-semibold rounded-lg transition duration-300 shadow-lg flex items-center justify-center bg-red-600 text-white hover:bg-red-700 focus:ring-4 focus:ring-red-300 transform active:scale-95 ${isGenerating ? 'opacity-60 cursor-not-allowed' : ''}`}
                >
                    {isGenerating ? (
                        <ArrowPathIcon className="h-5 w-5 mr-2 animate-spin" />
                    ) : (
                        <DocumentArrowDownIcon className="h-5 w-5 mr-2" />
                    )}
                    {isGenerating ? 'Generating...' : 'Download PDF'}
                </button>
            </div>
        </div>
    );
};

// --- MAIN APPLICATION COMPONENT ---

export default function PayrollReportsApp() {
    const [selectedEmployee, setSelectedEmployee] = useState('all');
    const [selectedPeriod, setSelectedPeriod] = useState('current');
    const [isExporting, setIsExporting] = useState(false);
    const [isFiltering, setIsFiltering] = useState(false);
    const [activeTab, setActiveTab] = useState('payroll');

    const employeeOptions = [
        { label: "All Employees", value: "all" },
        ...MOCK_EMPLOYEES.map(e => ({ label: e.name, value: e.id }))
    ];

    const periodOptions = [
        { label: "Current Month (Nov 2025)", value: "current" },
        { label: "Last Month (Oct 2025)", value: "last" },
        { label: "Q3 2025", value: "q3" },
    ];

    const handleExport = () => {
        setIsExporting(true);
        console.log(`Exporting payroll report for employee: ${selectedEmployee} and period: ${selectedPeriod}`);
        // Simulate download delay
        setTimeout(() => {
            setIsExporting(false);
            // In a real app, trigger file download here
            alert("Report Exported Successfully!");
        }, 1500);
    };

    const handleFilterChange = (setter, value) => {
        setter(value);
        setIsFiltering(true);
        // Simulate data fetching delay
        setTimeout(() => setIsFiltering(false), 800);
    };

    const filteredData = useMemo(() => {
        if (selectedEmployee === 'all' || isFiltering) return MOCK_DETAILED_REPORTS;
        
        const employeeId = parseInt(selectedEmployee);
        return MOCK_DETAILED_REPORTS.filter(report => report.id === employeeId);

    }, [selectedEmployee, MOCK_DETAILED_REPORTS, isFiltering]);


    return (
        <div 
            className="min-h-screen bg-gray-50 p-4 sm:p-6 lg:p-10"
            // Setting the font family preference for the enterprise look
            style={{ fontFamily: "Poppins, Roboto, sans-serif" }}
        >
            {/* Page Header and Controls */}
            <div className="mb-8 border-b border-gray-200 pb-4 flex flex-col sm:flex-row justify-between items-start sm:items-center">
                <div className="mb-4 sm:mb-0">
                    <h1 className="text-3xl sm:text-4xl font-extrabold text-gray-900 font-poppins flex items-center">
                        <CalculatorIcon className="h-8 w-8 mr-3 text-indigo-600"/>
                        Enterprise Payroll Reports
                    </h1>
                    <p className="text-gray-500 mt-1 text-md font-roboto">
                        Comprehensive financial summaries and detailed payout statements for the selected period.
                    </p>
                </div>
                <button 
                    onClick={handleExport}
                    disabled={isExporting}
                    className={`px-6 py-2.5 text-base font-semibold rounded-lg transition duration-300 shadow-xl flex items-center justify-center 
                        bg-teal-600 text-white hover:bg-teal-700 focus:ring-4 focus:ring-teal-300 transform active:scale-95 w-full sm:w-auto ${isExporting ? 'opacity-60 cursor-not-allowed' : ''}`}
                >
                    {isExporting ? (
                        <ArrowPathIcon className="h-5 w-5 mr-2 animate-spin" />
                    ) : (
                        <ArrowDownCircleIcon className="h-5 w-5 mr-2" />
                    )}
                    {isExporting ? 'Generating...' : 'Export PDF/CSV'}
                </button>
            </div>

            {/* 1. Key Metrics Summary */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
                <PayrollMetricCard 
                    title="Total Gross Payout (INR)" 
                    value={MOCK_PAYROLL_SUMMARY.totalGrossPayout} 
                    icon={CurrencyDollarIcon}
                    colorClass="text-indigo-600" 
                    footer="Total earnings before all deductions."
                />
                <PayrollMetricCard 
                    title="Total Net Payout (INR)" 
                    value={MOCK_PAYROLL_SUMMARY.totalNetPayout} 
                    icon={PaperClipIcon} 
                    colorClass="text-green-600" 
                    footer="Total amount disbursed to employees."
                />
                <PayrollMetricCard 
                    title="Total Tax Deducted (INR)" 
                    value={MOCK_PAYROLL_SUMMARY.totalTaxDeducted} 
                    icon={ArchiveBoxArrowDownIcon} 
                    colorClass="text-red-600" 
                    footer="Taxes and statutory deductions."
                />
                <PayrollMetricCard 
                    title="Total Reimbursements (INR)" 
                    value={MOCK_PAYROLL_SUMMARY.totalReimbursements} 
                    icon={ClockIcon} 
                    colorClass="text-yellow-600" 
                    footer="Non-taxable and expense refunds."
                />
            </div>

            {/* 2. Filter Bar */}
            <div className="bg-white p-5 rounded-xl shadow-lg border border-gray-100 flex flex-col md:flex-row items-stretch md:items-end gap-4 mb-8">
                <div className="flex-grow grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <CustomSelect
                        label="Select Reporting Period"
                        options={periodOptions}
                        selected={selectedPeriod}
                        onChange={(e) => handleFilterChange(setSelectedPeriod, e.target.value)}
                    />
                    <CustomSelect
                        label="Filter by Employee Name"
                        options={employeeOptions}
                        selected={selectedEmployee}
                        onChange={(e) => handleFilterChange(setSelectedEmployee, e.target.value)}
                    />
                </div>
            </div>

            {/* 3. Tab Navigation */}
            <div className="bg-white p-1 rounded-xl shadow-lg border border-gray-100 mb-6">
                <div className="flex space-x-1">
                    <button
                        onClick={() => setActiveTab('payroll')}
                        className={`px-6 py-3 text-sm font-semibold rounded-lg transition duration-300 flex items-center ${
                            activeTab === 'payroll'
                                ? 'bg-indigo-600 text-white shadow-lg'
                                : 'text-gray-600 hover:bg-gray-100'
                        }`}
                    >
                        <CalculatorIcon className="h-5 w-5 mr-2" />
                        Payroll Details
                    </button>
                    <button
                        onClick={() => setActiveTab('activity')}
                        className={`px-6 py-3 text-sm font-semibold rounded-lg transition duration-300 flex items-center ${
                            activeTab === 'activity'
                                ? 'bg-indigo-600 text-white shadow-lg'
                                : 'text-gray-600 hover:bg-gray-100'
                        }`}
                    >
                        <ClockIcon className="h-5 w-5 mr-2" />
                        Activity Logs
                    </button>
                    <button
                        onClick={() => setActiveTab('reports')}
                        className={`px-6 py-3 text-sm font-semibold rounded-lg transition duration-300 flex items-center ${
                            activeTab === 'reports'
                                ? 'bg-indigo-600 text-white shadow-lg'
                                : 'text-gray-600 hover:bg-gray-100'
                        }`}
                    >
                        <DocumentArrowDownIcon className="h-5 w-5 mr-2" />
                        Reports
                    </button>
                </div>
            </div>

            {/* 4. Tab Content */}
            {activeTab === 'payroll' && <DetailedPayrollReport data={filteredData} isFiltering={isFiltering}/>}
            {activeTab === 'activity' && <ActivityLogs />}
            {activeTab === 'reports' && <Reports />}
            
            {/* 5. Footer */}
            <div className="mt-10 pt-4 border-t border-gray-200 text-center">
                <p className="text-sm text-gray-500 font-roboto">
                    All financial data is processed in accordance with the current company and statutory regulations.
                </p>
            </div>

        </div>
    );
}